import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * DeleteConfirmationDialog - Reusable confirmation dialog for delete actions
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {string} [title] - Dialog title (default: "Confirm Deletion")
 * @param {string} [message] - Primary message shown to the user
 * @param {string} [confirmLabel] - Label for the confirm button (default: "Delete")
 * @param {boolean} [isDanger] - Whether to show the warning color scheme (default: true)
 * @param {Array}  [items] - GL key items with dependency info (optional):
 *   [{ gl_key, account_name, dependencies: { drivers, changes, formulas } }]
 * @param {function} onConfirm - Called when user confirms
 * @param {function} onCancel - Called when user cancels
 */
const DeleteConfirmationDialog = ({
  open,
  title = 'Confirm Deletion',
  message,
  confirmLabel,
  isDanger = true,
  items = [],
  onConfirm,
  onCancel,
}) => {
  const itemsWithDependencies = items.filter(
    item => item.dependencies && (
      (item.dependencies.drivers?.length > 0) ||
      (item.dependencies.changes?.length > 0) ||
      (item.dependencies.formulas?.length > 0)
    )
  );
  const hasDependencies = itemsWithDependencies.length > 0;

  const defaultMessage = `Are you sure you want to delete ${items.length} ${items.length === 1 ? 'account' : 'accounts'}?`;

  const displayMessage = message || defaultMessage;
  const buttonLabel = confirmLabel || (hasDependencies ? 'Delete Anyway' : 'Delete');

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isDanger && <DeleteIcon color="error" fontSize="small" />}
        {title}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: hasDependencies ? 2 : 0 }}>
          {displayMessage}
        </Typography>

        {/* GL key dependency warnings */}
        {hasDependencies && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon sx={{ color: '#856404', mr: 1 }} />
              <Typography variant="subtitle2" sx={{ color: '#856404', fontWeight: 'bold' }}>
                Warning: The following accounts have dependencies:
              </Typography>
            </Box>
            <List dense disablePadding sx={{ maxHeight: '200px', overflowY: 'auto' }}>
              {itemsWithDependencies.map((item, index) => (
                <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    • {item.gl_key} - {item.account_name}
                  </Typography>
                  {item.dependencies.drivers?.length > 0 && (
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                      – Used as driver by {item.dependencies.drivers.length} account{item.dependencies.drivers.length !== 1 ? 's' : ''}
                      {item.dependencies.drivers.length <= 3 && `: ${item.dependencies.drivers.map(d => d.gl_key).join(', ')}`}
                    </Typography>
                  )}
                  {item.dependencies.changes?.length > 0 && (
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                      – Referenced in {item.dependencies.changes.length} change{item.dependencies.changes.length !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {item.dependencies.formulas?.length > 0 && (
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                      – Referenced in {item.dependencies.formulas.length} formula{item.dependencies.formulas.length !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
            <Typography variant="body2" sx={{ mt: 1, color: '#856404', fontStyle: 'italic' }}>
              Deleting these accounts may cause errors in dependent accounts and changes.
            </Typography>
          </Box>
        )}

        {isDanger && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color={isDanger ? 'error' : 'primary'} autoFocus>
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
